// src/contexts/MessagingContext.tsx
import React, { createContext, useContext, useState, ReactNode } from 'react';

interface TargetUser {
  id: string;
  username: string;
  first_name: string;
  last_name: string;
  profile_picture?: string;
}

interface MessagingContextType {
  // Modal states
  showMessagingModal: boolean;
  showChatModal: boolean;
  conversationId: string | null;
  targetUser: TargetUser | null;
  
  // Functions to control modals
  openMessagingModal: () => void;
  openChatWithUser: (user: TargetUser) => void;
  openChatWithConversation: (conversationId: string, user?: TargetUser) => void;
  closeAllModals: () => void;
}

const MessagingContext = createContext<MessagingContextType | undefined>(undefined);

export const MessagingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [showMessagingModal, setShowMessagingModal] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [targetUser, setTargetUser] = useState<TargetUser | null>(null);

  const openMessagingModal = () => {
    setShowMessagingModal(true);
    setShowChatModal(false);
  };

  const openChatWithUser = (user: TargetUser) => {
    setTargetUser(user);
    setConversationId(null);
    setShowChatModal(true);
    setShowMessagingModal(false);
  };

  const openChatWithConversation = (convId: string, user?: TargetUser) => {
    setConversationId(convId);
    setTargetUser(user || null);
    setShowChatModal(true);
    setShowMessagingModal(false);
  };

  const closeAllModals = () => {
    setShowMessagingModal(false);
    setShowChatModal(false);
    setConversationId(null);
    setTargetUser(null);
  };

  return (
    <MessagingContext.Provider
      value={{
        showMessagingModal,
        showChatModal,
        conversationId,
        targetUser,
        openMessagingModal,
        openChatWithUser,
        openChatWithConversation,
        closeAllModals,
      }}
    >
      {children}
    </MessagingContext.Provider>
  );
};

export const useMessaging = () => {
  const context = useContext(MessagingContext);
  if (context === undefined) {
    throw new Error('useMessaging must be used within a MessagingProvider');
  }
  return context;
};